import cx from 'classnames';
import {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {isFabricWebview} from '~/common/utils/webview';
import {isInRsvpPhoneNumberCollectControl} from '~/ViewInvitation/common/experiments';
import {RSVPModalProps} from '~/ViewInvitation/common/RSVPModalTestAB';
import {selectEventTemplateName} from '~/ViewInvitation/selectors/eviteEvent';
import {selectHasRsvpd, selectNotifications} from '~/ViewInvitation/selectors/rsvp';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {getUserData} from '~library/utils/auth/utils';
import {CecPreferences, ChuckECheese} from './steps/ChuckECheese/ChuckECheese';
import {CollectInfo} from './steps/CollectInfo/CollectInfo';
import {CollectInfoVariant} from './steps/CollectInfoVariant/CollectInfoVariant';
import {Animation} from './steps/components/ConfettiAnimation';
import {Payload} from './types';

import './RSVPModalVariantAB.sass';

export const RSVPModalVariantAB = ({open, status, onClose, onSubmitRSVP}: RSVPModalProps) => {
  const isMediumAndUpScreen = useIsMediumAndUp();
  const history = useHistory();
  const location = useLocation();
  const templateName = useSelector(selectEventTemplateName);
  const isChuckECheese = templateName.includes('chuckecheese');
  const [step, setStep] = useState(0);
  const [stepsCounter, setStepsCounter] = useState(0);
  const [payload, setPayload] = useState({} as Payload);
  const hasRsvpd = useSelector(selectHasRsvpd);
  const notifications = useSelector(selectNotifications);
  const userData = getUserData();
  const searchParams = new URLSearchParams(window.location.search);
  const gid = searchParams.get('gid');
  const guestId = userData.guest_id || userData.guestId || gid || window.guest_id;
  const isWebview = isFabricWebview();
  const inControl = isInRsvpPhoneNumberCollectControl({
    readOnly: !open || isWebview,
  });

  const onClickCloseHandler = useCallback(async () => {
    setStep(0);
    onClose();
  }, [onClose]);

  // This will inject the user ID, and notification options and into the RSVP payload
  const onSubmitHandler = useCallback(
    (info: Payload) => {
      // biome-ignore-start lint/style/useNamingConvention: existing error
      let notificationOptions = notifications;

      if (!hasRsvpd) {
        notificationOptions = {
          email_new_comments: true,
          email_new_rsvp: false,
          push_new_comments: true,
          push_new_rsvp: true,
          notify_only_my_activity: true,
          notify_only_my_activity_push: true,
        };
      }

      if (inControl) {
        notificationOptions = info.notification_options;
      }

      return onSubmitRSVP(
        {
          ...info,
          guest_id: guestId,
          notification_options: notificationOptions,
        },
        true
      );
      // biome-ignore-end lint/style/useNamingConvention: existing error
    },
    [guestId, hasRsvpd, inControl, notifications, onSubmitRSVP]
  );

  const saveRSVP = useCallback(
    async (info: Payload) => {
      setPayload(info);
      return onSubmitHandler(info);
    },
    [onSubmitHandler]
  );

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  const onGoForwardClick = useCallback(() => {
    const nextStep = step + 1;
    if (nextStep >= stepsCounter) {
      history.replace(`/frp/rsvp?gid=${window.guest_id}`, location.state);
    } else {
      setStep(nextStep);
    }
  }, [history, location.state, step, stepsCounter]);

  const onCecSave = useCallback(
    async (newInfo: CecPreferences) => {
      await onSubmitHandler({...payload, ...newInfo});
      onGoForwardClick();
    },
    [onGoForwardClick, onSubmitHandler, payload]
  );

  const storeInfoHandler = useCallback(
    async (info: Payload) => {
      try {
        await saveRSVP(info);
        onGoForwardClick();
      } catch (error) {
        evite.error(error);
      }
    },
    [saveRSVP, onGoForwardClick]
  );

  const steps = useMemo(() => {
    const stepsList: ReactElement[] = [];

    if (inControl) {
      stepsList.push(
        <CollectInfo
          key="collect-info"
          status={status}
          onCtaClick={storeInfoHandler}
          onClickClose={onClickCloseHandler}
        />
      );
    } else {
      stepsList.push(
        <CollectInfoVariant
          key="collect-info-variant"
          status={status}
          onCtaClick={storeInfoHandler}
          onClickClose={onClickCloseHandler}
        />
      );
    }

    if (isChuckECheese) {
      stepsList.push(
        <ChuckECheese key="chuck-e-cheese" onCtaClick={onCecSave} onCloseClick={onGoForwardClick} />
      );
    }

    setStepsCounter(stepsList.length);

    return stepsList;
  }, [
    status,
    storeInfoHandler,
    onClickCloseHandler,
    isChuckECheese,
    onCecSave,
    onGoForwardClick,
    inControl,
  ]);

  const renderModalContent = useCallback(
    () => (
      <>
        <Animation status={status} step={step} />
        {steps.map((stepComponent, index) => (
          <div
            key={stepComponent.key || `step-${index}`}
            className={cx('RSVP-modal__content', {
              'RSVP-modal__content--active': index === step,
            })}
          >
            {stepComponent}
          </div>
        ))}
      </>
    ),
    [status, step, steps]
  );

  if (isMediumAndUpScreen) {
    return (
      <ResponsiveModal
        isModal
        isOpen={open}
        onClose={() => null}
        overlayClassName="RSVP-modal"
        showHeader={false}
      >
        {renderModalContent()}
      </ResponsiveModal>
    );
  }

  return (
    <BottomSheet
      in={open}
      className="custom-bottom-sheet__overlay__content"
      content={renderModalContent()}
      aboveOtherModals
    />
  );
};
